import { Component, Input, input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Nullable } from '@core/models/nullable';
import { InvestorTab } from '@core/models/types';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MessageService } from 'primeng/api';
import { ChromeExtensionService } from 'src/app/services/chrome-extension/chrome-extension.service';
import { PromptAPIResponse, PromptCategoryAPIResponse, PromptService } from 'src/app/services/prompt-service/prompt.service';
import { RouterService } from 'src/app/services/router/router.service';
import { TabUtilsService } from 'src/app/services/tab-utils.service';

export type Prompt = {
  name?: Nullable<string>;
  description?: Nullable<string>;
  category?: Nullable<number>;
  submittedBy?: Nullable<string>;
};

export type PromptCategory = {
  name: string,
  value?: number
};

type Environment = {
  title: string;
  variables: Variable[];
};

type Variable = {
  name: string;
  type: any;
  value: string;
};

@Component({
  selector: 'bpc-prompt',
  templateUrl: './prompt.component.html',
  styleUrls: ["./prompt.component.scss"],
})
export class PromptComponent implements OnInit {
  prompts!: Prompt[];
  selectedCategory?: number = undefined;
  searchQuery: string = "";
  selectedEnvironment!: string;
  displayAddPromptDialog: boolean = false;
  displayAddEnvironmentDialog: boolean = false;
  newPrompt: Prompt = {};
  newEnvironment: Environment = { title: '', variables: [] };
  isViewMode: boolean = false;
  promptsLoading: boolean = true;
  categoriesLoading: boolean = true;
  promptSubmitting: boolean = false;

  variableTypes: any[] = [
    { label: 'String', value: 'string' },
    { label: 'Array', value: 'array' }
  ];

  environments: any[] = [
    { label: 'No Environment', value: 'none' },
    { label: 'Folio3', value: 'Folio3' },
    { label: 'LucidLink', value: 'LucidLink' }
  ];

  environmentsData: Environment[] = [
    {
      "title": "Folio3",
      "variables": [
        {
          "name": "company",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Folio3"
        },
        {
          "name": "investor",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Dummy Investor"
        },
        {
          "name": "fund_name",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Folio3 Fund"
        },
        {
          "name": "investor_name",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Emily Johnson"
        },
        {
          "name": "sector",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Technology"
        },
        {
          "name": "investment",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Folio3 Investment"
        },
        {
          "name": "years",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "5"
        },
        {
          "name": "industry",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Technology"
        }
      ]
    },
    {
      "title": "LucidLink",
      "variables": [
        {
          "name": "company",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "LucidLink"
        },
        {
          "name": "investor",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "LucidLink Investor"
        },
        {
          "name": "fund_name",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "LucidLink Fund"
        },
        {
          "name": "investor_name",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "LucidLink Investor"
        },
        {
          "name": "sector",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Technology"
        },
        {
          "name": "investment",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "LucidLink Investment"
        },
        {
          "name": "years",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "3"
        },
        {
          "name": "industry",
          "type": {
            "label": "String",
            "value": "string"
          },
          "value": "Technology"
        }
      ]
    }
  ];

  promptCategories: PromptCategory[] = [];

  type : string | null = "";
  
  childTabs: any[] =[
    {label: "Chatbot", route: "chatbot", index: 0},
    {label: "Prompt Library", route: "prompts", index: 1},
  ]
  @Input() isChatGPT: boolean = false;

  get categories(){
    return this.promptCategories.filter(item => item.value != undefined);
  }

    
  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }

  get isExtension(): boolean {
    return this.chromeExtensionService.isExtension.getValue();
  }

  constructor(
    private messageService: MessageService, 
    private promptService: PromptService,
    private deviceDetectorService: DeviceDetectorService,
    private chromeExtensionService: ChromeExtensionService,
    private routerService: RouterService,
    private tabUtilsService: TabUtilsService,
  ) { }

  ngOnInit(): void {
    this.routerService.updateCurrentTab(InvestorTab.DEEP_CHAT);
    const url = this.routerService.url;

    if (url) {
      this.type = url.substring(url.lastIndexOf('/') + 1) ?? "";
    }
    
    this.getCategories();
    this.getData();
  }

  getCategories(){
    this.categoriesLoading = true;
    this.promptService.getPromptsCategories().subscribe(response=>{
      if(response.data){
        this.promptCategories = response.data.map((item: PromptCategoryAPIResponse): PromptCategory => ({
          name: item.category,
          value: item.promptCategoryId
        })); 
        this.promptCategories.push({value: undefined, name: "All"})
        this.selectedCategory = undefined;
        this.categoriesLoading = false;
      }
      else{
        this.categoriesLoading = false;
        this.showErrorToastMessage("Categories could not be loaded");
        this.promptCategories = [];
        this.promptCategories.push({value: undefined, name: "All"})
      }
    })
  }

  pasteToLLM(prompt: string) {
    window.parent.postMessage({ type: 'PASTE_TO_LLM', text: prompt }, '*');
  }

  copyToClipboard(prompt: string) {
    navigator.clipboard.writeText(prompt).then(() => {
      this.messageService.add({ key: "home-bc", severity: "success", detail: 'Prompt copied to clipboard', life: 2000 });
    });
  }

  protected showErrorToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "error", detail: message, life: 10_000 });
  }

  protected showSuccessToastMessage(message: string) {
    this.messageService.add({ key: "home-bc", severity: "success", detail: message, life: 5_000 });
  }

  getData() {
    this.promptsLoading = true;
    this.promptService.getPrompts(this.selectedCategory).subscribe((response) =>{
        if (response.data){
          this.prompts = response.data.map((item: PromptAPIResponse): Prompt => ({
            name: item.title,
            description: item.promptText,
            category: item.promptCategoryId,
            submittedBy: item.user?.name 
          })); 
          this.promptsLoading = false;
        }else{
          this.showErrorToastMessage("Prompts could not be loaded");
          this.prompts = [];
          this.promptsLoading = false;
        }
      }
    );
  }

  onSelectionChange(event: any) {
    this.selectedCategory = event.value;

    this.applyEnvironmentVariables();
  }

  showAddPromptDialog() {
    this.displayAddPromptDialog = true;
  }

  savePrompt(form: NgForm) {
    this.promptSubmitting = true;
    this.promptService.addPrompt(this.newPrompt).subscribe(
      response => {
        if (response.data.result){
          this.getData();
          this.displayAddPromptDialog = false;
          this.newPrompt = {};
          form.resetForm();
          this.showSuccessToastMessage("Prompt added!");
          this.promptSubmitting = false;
          this.tabUtilsService.refreshData.next(); // Fix scroll for ipad.
        }
        else{
          this.showErrorToastMessage("Prompt could not be added, try again!");
          this.promptSubmitting = false;
          form.resetForm();
          this.tabUtilsService.refreshData.next(); // Fix scroll for ipad.
        }
      }
    )
  }

  showAddEnvironmentDialog() {
    this.isViewMode = false;
    this.newEnvironment = {
      title: '', variables: [
        { name: '', type: this.variableTypes[0], value: '' }
      ]
    };
    this.displayAddEnvironmentDialog = true;
  }

  addVariable() {
    this.newEnvironment.variables.push({ name: '', type: this.variableTypes[0], value: '' });
  }

  saveEnvironment() {
    // Call backend API to save the environment
    // Assuming the backend API returns the saved environment with an ID

    // check if the environment already exists
    const existingEnvironment = this.environmentsData.find(env => env.title === this.newEnvironment.title);
    if (existingEnvironment) {
      // Update the existing environment
      existingEnvironment.variables = this.newEnvironment.variables;
      this.displayAddEnvironmentDialog = false;
      return;
    }
    const savedEnvironment = { label: this.newEnvironment.title, value: this.newEnvironment.title };
    this.environments.push(savedEnvironment);
    this.newEnvironment = { title: '', variables: [] };
    this.displayAddEnvironmentDialog = false;
  }

  onEnvironmentChange(event: any) {
    this.selectedEnvironment = event.value;
    this.applyEnvironmentVariables();
  }

  viewEnvironment() {
    if (!this.selectedEnvironment || this.selectedEnvironment === 'none') {
      return;
    }

    const environment = this.environments.find(env => env.value === this.selectedEnvironment);
    if (environment) {
      this.isViewMode = true;
      this.newEnvironment.title = environment.label;
      // Assuming you have a method to fetch environment details from the backend
      this.newEnvironment.variables = this.getEnvironmentVariables(this.selectedEnvironment);
      this.displayAddEnvironmentDialog = true;
    }
  }

  getEnvironmentVariables(environmentTitle: string): Variable[] {
    // Fetch the environment variables from the backend based on the environment title
    // This is a placeholder implementation
    const environment = this.environmentsData.find(env => env.title === environmentTitle);
    return environment ? environment.variables : [];
  }

  applyEnvironmentVariables() {
    if (!this.selectedEnvironment || this.selectedEnvironment === 'none') {
      this.getData();
      return;
    }

    const environment = this.environmentsData.find(env => env.title === this.selectedEnvironment);
    // if (environment) {
    //   this.prompts = this.getData().map(prompt => {
    //     let description = prompt.description;
    //     environment.variables.forEach(variable => {
    //       const regex = new RegExp(`{{${variable.name}}}`, 'g');
    //       description = description.replace(regex, variable.value);
    //     });
    //     return { ...prompt, description };
    //   });
    // }
  }

  filterData(e: any){
    this.selectedCategory = undefined;
    this.promptsLoading = true;
    this.promptService.getPrompts(this.selectedCategory, e.target.value).subscribe((response) =>{
        if (response.data){
          this.prompts = response.data.map((item: PromptAPIResponse): Prompt => ({
            name: item.title,
            description: item.promptText,
            category: item.promptCategoryId,
            submittedBy: item.user?.name 
          })); 
          this.promptsLoading = false;
        }
      }
    );
  }

  get submitEnabled(){
    return this.newPrompt.category || this.newPrompt.description || this.newPrompt.name;
  }

  closeForm(form: NgForm){
    this.displayAddPromptDialog = false;
    this.newPrompt = {};
    form.resetForm();
  }
}
